import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, GripVertical, ExternalLink, ArrowRight, Eye, Loader2, Star, ChevronUp, ChevronDown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import MediaUploader from '../../components/MediaUploader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBioLinks = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const { token } = useAuth();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cms/bio-profile`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setProfile(await res.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchProfile();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/bio-profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(profile),
      });
      if (res.ok) {
        setProfile(await res.json());
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const updateLink = (index, field, value) => {
    const links = [...profile.links];
    links[index] = { ...links[index], [field]: value };
    setProfile({ ...profile, links });
  };

  const addLink = () => {
    const links = [...profile.links, {
      id: crypto.randomUUID().slice(0, 8),
      title: '',
      url: '',
      icon: 'link',
      is_external: false,
      highlight: false,
      order: profile.links.length,
    }];
    setProfile({ ...profile, links });
  };

  const removeLink = (index) => {
    const links = profile.links.filter((_, i) => i !== index);
    setProfile({ ...profile, links });
  };

  const moveLink = (index, direction) => {
    const links = [...profile.links];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= links.length) return;
    [links[index], links[newIndex]] = [links[newIndex], links[index]];
    links.forEach((l, i) => l.order = i);
    setProfile({ ...profile, links });
  };

  const updateTestimonial = (index, field, value) => {
    const testimonials = [...(profile.testimonials || [])];
    testimonials[index] = { ...testimonials[index], [field]: value };
    setProfile({ ...profile, testimonials });
  };

  const addTestimonial = () => {
    const testimonials = [...(profile.testimonials || []), { quote: '', author: '' }];
    setProfile({ ...profile, testimonials });
  };

  const removeTestimonial = (index) => {
    const testimonials = (profile.testimonials || []).filter((_, i) => i !== index);
    setProfile({ ...profile, testimonials });
  };

  const inputClass = "w-full bg-navy border border-cream/10 rounded-lg px-3 py-2 text-sm text-cream placeholder-cream/20 focus:outline-none focus:ring-1 focus:ring-gold/30";

  if (loading) return <div className="py-16 text-center text-cream/20">Loading...</div>;
  if (!profile) return <div className="py-16 text-center text-cream/30">Failed to load bio profile</div>;

  return (
    <div data-testid="admin-bio-links">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-2xl text-cream font-semibold">Bio Links</h1>
          <p className="text-cream/30 text-sm mt-1">Edit your social bio page</p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/bio-links" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 border border-cream/10 text-cream/50 text-xs rounded-lg hover:border-gold/20 hover:text-gold" data-testid="preview-bio-link">
            <Eye size={14} /> Preview
          </a>
          <button onClick={handleSave} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 bg-gold text-navy text-xs font-semibold rounded-lg hover:bg-gold-light disabled:opacity-50" data-testid="save-bio-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <><Star size={14} /> Saved!</> : <><Save size={14} /> Save Changes</>}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Info */}
        <div className="space-y-4">
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-5">
            <p className="text-[10px] uppercase tracking-wider text-gold/50 mb-4 font-semibold">Profile</p>

            {/* Profile Image */}
            <div className="mb-4">
              <label className="block text-[10px] text-cream/20 mb-1.5 uppercase tracking-wider">Profile Image</label>
              {profile.profile_image && (
                <div className="mb-2 flex justify-center">
                  <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-gold/30">
                    <img src={profile.profile_image} alt="Profile" className="w-full h-full object-cover" />
                  </div>
                </div>
              )}
              <MediaUploader
                accept="image"
                folder="bio"
                label="Upload profile photo"
                onUpload={(url) => setProfile({ ...profile, profile_image: url })}
              />
              <input value={profile.profile_image || ''} onChange={(e) => setProfile({ ...profile, profile_image: e.target.value })} placeholder="Or paste URL..." className={`${inputClass} mt-1.5 text-xs`} data-testid="profile-image-input" />
            </div>

            {/* Logo Image */}
            <div className="mb-4">
              <label className="block text-[10px] text-cream/20 mb-1.5 uppercase tracking-wider">Logo Image</label>
              {profile.logo_image && (
                <div className="mb-2 flex justify-center">
                  <img src={profile.logo_image} alt="Logo" className="h-10 w-auto" />
                </div>
              )}
              <MediaUploader
                accept="image"
                folder="bio"
                label="Upload logo"
                onUpload={(url) => setProfile({ ...profile, logo_image: url })}
              />
              <input value={profile.logo_image || ''} onChange={(e) => setProfile({ ...profile, logo_image: e.target.value })} placeholder="Or paste URL..." className={`${inputClass} mt-1.5 text-xs`} data-testid="logo-image-input" />
            </div>

            {/* Name */}
            <div className="mb-3">
              <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Name</label>
              <input value={profile.name || ''} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className={inputClass} data-testid="bio-name-input" />
            </div>

            {/* Tagline */}
            <div className="mb-3">
              <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Tagline</label>
              <input value={profile.tagline || ''} onChange={(e) => setProfile({ ...profile, tagline: e.target.value })} className={inputClass} data-testid="bio-tagline-input" />
            </div>

            {/* Subtitle */}
            <div>
              <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Subtitle</label>
              <textarea value={profile.subtitle || ''} onChange={(e) => setProfile({ ...profile, subtitle: e.target.value })} rows={2} className={`${inputClass} resize-none`} data-testid="bio-subtitle-input" />
            </div>
          </div>

          {/* Social Profile */}
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-5">
            <p className="text-[10px] uppercase tracking-wider text-gold/50 mb-4 font-semibold">Social Profile</p>
            <p className="text-cream/15 text-[10px] mb-3">Shown on the /social page header</p>

            {/* Social Avatar */}
            <div className="mb-4">
              <label className="block text-[10px] text-cream/20 mb-1.5 uppercase tracking-wider">Social Avatar</label>
              <p className="text-cream/10 text-[9px] mb-1.5">Separate image for the social page. Falls back to profile image if empty.</p>
              {profile.social_avatar && (
                <div className="mb-2 flex justify-center">
                  <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gold/30">
                    <img src={profile.social_avatar} alt="Social Avatar" className="w-full h-full object-cover" />
                  </div>
                </div>
              )}
              <MediaUploader
                accept="image"
                folder="bio"
                label="Upload social avatar"
                onUpload={(url) => setProfile({ ...profile, social_avatar: url })}
              />
              <input value={profile.social_avatar || ''} onChange={(e) => setProfile({ ...profile, social_avatar: e.target.value })} placeholder="Or paste URL..." className={`${inputClass} mt-1.5 text-xs`} data-testid="social-avatar-input" />
            </div>

            <div className="mb-3">
              <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Title / Role</label>
              <input value={profile.social_title || ''} onChange={(e) => setProfile({ ...profile, social_title: e.target.value })} placeholder="e.g. Paediatrician • Author • Founder, Third Culture Leadership" className={inputClass} data-testid="social-title-input" />
            </div>

            <div className="mb-3">
              <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Bio</label>
              <textarea value={profile.social_bio || ''} onChange={(e) => setProfile({ ...profile, social_bio: e.target.value })} rows={3} placeholder="Short bio for social page..." className={`${inputClass} resize-none`} data-testid="social-bio-input" />
            </div>

            <div className="grid grid-cols-2 gap-2 mb-3">
              <div>
                <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Followers</label>
                <input value={profile.social_followers || ''} onChange={(e) => setProfile({ ...profile, social_followers: e.target.value })} placeholder="4.8K" className={inputClass} data-testid="social-followers-input" />
              </div>
              <div>
                <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Connections</label>
                <input value={profile.social_connections || ''} onChange={(e) => setProfile({ ...profile, social_connections: e.target.value })} placeholder="500+" className={inputClass} data-testid="social-connections-input" />
              </div>
            </div>

            <div>
              <label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Website</label>
              <input value={profile.social_website || ''} onChange={(e) => setProfile({ ...profile, social_website: e.target.value })} placeholder="thirdcultureleadership.co.uk" className={inputClass} data-testid="social-website-input" />
            </div>
          </div>

          {/* Testimonials */}
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[10px] uppercase tracking-wider text-gold/50 font-semibold">Testimonials</p>
              <button onClick={addTestimonial} className="text-gold/50 hover:text-gold text-xs"><Plus size={12} className="inline" /> Add</button>
            </div>
            <div className="space-y-3">
              {(profile.testimonials || []).map((t, i) => (
                <div key={i} className="bg-navy-deep rounded-lg p-3 relative group">
                  <button onClick={() => removeTestimonial(i)} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-cream/15 hover:text-red-400 transition-all"><Trash2 size={12} /></button>
                  <textarea value={t.quote} onChange={(e) => updateTestimonial(i, 'quote', e.target.value)} placeholder="Quote..." rows={2} className="w-full bg-transparent text-cream/50 text-xs italic resize-none outline-none mb-1.5 placeholder-cream/15" />
                  <input value={t.author} onChange={(e) => updateTestimonial(i, 'author', e.target.value)} placeholder="Author name & title..." className="w-full bg-transparent text-gold/60 text-[10px] outline-none placeholder-cream/15" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Links Editor */}
        <div className="lg:col-span-2">
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[10px] uppercase tracking-wider text-gold/50 font-semibold">Links ({profile.links?.length || 0})</p>
              <button onClick={addLink} className="inline-flex items-center gap-1 px-3 py-1.5 bg-gold text-navy text-[10px] font-semibold rounded-lg hover:bg-gold-light" data-testid="add-link-btn">
                <Plus size={12} /> Add Link
              </button>
            </div>

            <div className="space-y-2">
              {(profile.links || []).map((link, i) => (
                <div key={link.id || i} className="bg-navy-deep rounded-lg p-3 flex items-start gap-3 group" data-testid={`bio-link-${i}`}>
                  {/* Reorder */}
                  <div className="flex flex-col gap-0.5 pt-1 flex-shrink-0">
                    <button onClick={() => moveLink(i, -1)} disabled={i === 0} className="text-cream/15 hover:text-gold disabled:opacity-20 transition-colors"><ChevronUp size={14} /></button>
                    <button onClick={() => moveLink(i, 1)} disabled={i === profile.links.length - 1} className="text-cream/15 hover:text-gold disabled:opacity-20 transition-colors"><ChevronDown size={14} /></button>
                  </div>

                  {/* Fields */}
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex gap-2">
                      <input value={link.title} onChange={(e) => updateLink(i, 'title', e.target.value)} placeholder="Link title..." className="flex-1 bg-navy border border-cream/[0.06] rounded px-2.5 py-1.5 text-sm text-cream placeholder-cream/15 outline-none focus:border-gold/20" />
                      <select value={link.icon || 'link'} onChange={(e) => updateLink(i, 'icon', e.target.value)} className="bg-navy border border-cream/[0.06] rounded px-2 py-1.5 text-[10px] text-cream/40 outline-none w-28">
                        <option value="link">Link</option>
                        <option value="clipboard-check">Assessment</option>
                        <option value="globe">Globe</option>
                        <option value="graduation-cap">Course</option>
                        <option value="book-open">Read</option>
                        <option value="book">Book</option>
                        <option value="smartphone">Phone</option>
                        <option value="headphones">Podcast</option>
                        <option value="file-text">Article</option>
                        <option value="mic">Speaking</option>
                        <option value="mail">Email</option>
                        <option value="linkedin">LinkedIn</option>
                        <option value="twitter">Twitter/X</option>
                        <option value="instagram">Instagram</option>
                        <option value="youtube">YouTube</option>
                      </select>
                    </div>
                    <div className="flex gap-2 items-center">
                      <input value={link.url} onChange={(e) => updateLink(i, 'url', e.target.value)} placeholder="URL..." className="flex-1 bg-navy border border-cream/[0.06] rounded px-2.5 py-1.5 text-xs text-cream/50 placeholder-cream/15 outline-none focus:border-gold/20" />
                      <label className="flex items-center gap-1 text-[9px] text-cream/25 whitespace-nowrap cursor-pointer">
                        <input type="checkbox" checked={link.is_external} onChange={(e) => updateLink(i, 'is_external', e.target.checked)} className="rounded border-cream/20 bg-navy text-gold focus:ring-gold/20 w-3 h-3" />
                        External
                      </label>
                      <label className="flex items-center gap-1 text-[9px] text-cream/25 whitespace-nowrap cursor-pointer">
                        <input type="checkbox" checked={link.highlight} onChange={(e) => updateLink(i, 'highlight', e.target.checked)} className="rounded border-cream/20 bg-navy text-gold focus:ring-gold/20 w-3 h-3" />
                        Highlight
                      </label>
                    </div>
                  </div>

                  {/* Delete */}
                  <button onClick={() => removeLink(i)} className="p-1 text-cream/10 hover:text-red-400 transition-colors flex-shrink-0 mt-1" data-testid={`delete-link-${i}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>

            {(profile.links || []).length === 0 && (
              <div className="py-10 text-center text-cream/20 text-sm">No links yet. Click "Add Link" to get started.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBioLinks;
